<template>
	<AppPage :class="{ 'finance-mobile-content': isRefined }">
		<AddUserModal
			v-if="showAddUserModal"
			@hide-modal="showAddUserModal = false"
		/>
		<NotitiesModal v-if="showNotesModal" @hide-modal="showNotesModal = false" />
		<AppPageTitle
			full-width
			class="pb-0"
			:class="{ 'finance-refined-header': isRefined }"
		>
			<div>
				<h2>Financiering</h2>
			</div>
			<div v-if="role === 'intermediary'">
				<span v-if="showDevLabels">
					{{ loadingStore.unsavedChanges }}
				</span>
				<span
					v-if="loadingStore.dossierDeleteFailed"
					class="delete-failed-message"
				>
					Dossier kon niet verwijderd worden
				</span>

				<AppButton
					icon="edit_note"
					class="finance-header-action"
					aria-label="Notities"
					title="Bekijk de notities van het dossier"
					@click="showNotesModal = true"
					><span class="finance-action-label">Notities</span></AppButton
				>

				<AppButton
					icon="person_add"
					class="finance-header-action"
					aria-label="Klant toevoegen"
					title="Geef een klant toegang tot het dossier"
					@click="showAddUserModal = true"
					><span class="finance-action-label">Klant toevoegen</span></AppButton
				>
			</div>
			<template #actions>
				<AppTabBar class="finance-desktop-tabs" :tabs="visibleTabs" />
				<div v-if="isRefined" class="finance-mobile-navigation">
					<label for="finance-section-select">Financieringsonderdeel</label>
					<select
						id="finance-section-select"
						aria-label="Financieringsonderdeel"
						:value="route.name"
						@change="navigateToSection"
					>
						<option
							v-for="tab in visibleTabs"
							:key="tab.name"
							:value="tab.name"
						>
							{{ tab.label ?? tab.name }}
						</option>
					</select>
				</div>
			</template>
		</AppPageTitle>
		<RouterView v-slot="{ Component }">
			<KeepAlive include="PDFMakenPageWrap">
				<component :is="Component" />
			</KeepAlive>
		</RouterView>
	</AppPage>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useUserStore } from "@/store/useUserStore";
import { computed, ref } from "vue";
import { useLoadingStore } from "@/store/useLoadingStore";

import { useRoute, useRouter } from "vue-router";
import { isRefinedLayoutRoute } from "@/helpers/refinedLayoutRoutes";
import "@/style/ll-refined.scss";

const route = useRoute();
const router = useRouter();
const isRefined = computed(() => isRefinedLayoutRoute(route.name));
const userStore = useUserStore();
const { role } = storeToRefs(userStore);

const loadingStore = useLoadingStore();

const showAddUserModal = ref(false);
const showNotesModal = ref(false);

const tabs = [
	{ name: "Financieringsopzet", id: 0 },
	{ name: "Woning", id: 7 },
	{ name: "Hypotheek", label: "Hypotheek", id: 2 },
	{
		name: "Maximale Hypotheek",
		id: 3,
		condition: () => userStore.role === "intermediary",
	},
	{
		name: "PDF maken",
		id: 4,
		condition: () => userStore.role === "intermediary",
	},
	{ name: "Maandlasten", id: 5 },
	{
		name: "Optimalisatie",
		id: 6,
		condition: () => userStore.role === "intermediary",
	},
];

const visibleTabs = computed(() =>
	tabs.filter((tab) => tab.condition?.() ?? true),
);
function navigateToSection(event: Event) {
	const name = (event.target as HTMLSelectElement).value;
	if (visibleTabs.value.some((tab) => tab.name === name)) {
		void router.push({ name });
	}
}

const showDevLabels = import.meta.env.VITE_SHOW_DEV_LABELS === "true";
</script>

<style lang="scss">
.icon-muted {
	color: var(--accent-primary);
	cursor: pointer;

	&:hover {
		color: var(--accent-primary-hover);
	}
}

.delete-failed-message {
	color: var(--color-fail);
}
</style>
